import { useEffect, useRef, useState } from 'react';

const FAQS = [
  {
    q: '岡山市以外からでも利用できますか？',
    a: '出張買取は西日本を中心（岡山県・広島県・山口県・香川県・大阪府・京都府など）に対応しています。宅配買取は全国対応可能です。',
  },
  {
    q: '査定は無料ですか？',
    a: 'はい、査定は完全無料です。査定後のキャンセルも一切費用はかかりません。まずはお気軽にご相談ください。',
  },
  {
    q: 'どのような状態の品物でも買取可能ですか？',
    a: '傷や汚れがある商品、動作しない家電なども査定いたします。状態によっては買取できない場合もありますが、まずはご相談ください。',
  },
  {
    q: '本人確認書類は必ず必要ですか？',
    a: '古物営業法の規定により、買取の際は必ず本人確認が必要です。運転免許証・マイナンバーカード・健康保険証などをご用意ください。',
  },
  {
    q: '買取金額はいつ受け取れますか？',
    a: '店頭買取・出張買取の場合は、ご成約当日に現金でお支払いいたします。宅配買取は商品到着後3〜5営業日以内に銀行振込にて対応いたします。',
  },
  {
    q: '複数の品物をまとめて査定できますか？',
    a: 'もちろんです。複数点をまとめてお持ちいただくと、まとめ買取割増になる場合もございます。引越し時の大量処分などもお気軽にご相談ください。',
  },
  {
    q: '出張買取は費用がかかりますか？',
    a: '出張費・査定費は完全無料です。成約に至らなかった場合でも費用は一切いただきません。',
  },
  {
    q: '営業時間外の問い合わせは可能ですか？',
    a: 'ウェブサイトのお問い合わせフォームは24時間受付しております。翌営業日（9:00〜17:00）に担当スタッフよりご連絡いたします。',
  },
  {
    q: '宅配買取の梱包材はどうすれば良いですか？',
    a: '段ボールや緩衝材はお客様にてご用意をお願いしております。まずはお問い合わせフォームよりお申込みください。送付先をご案内いたします。',
  },
];

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="faq" ref={ref} className="py-20 md:py-28 bg-[#F8F9F7]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-[#B2AC88] text-xs font-medium tracking-widest uppercase">FAQ</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#2F3630] mt-3 mb-4" style={{ fontFamily: "'Shippori Mincho', serif" }}>
            よくある質問
          </h2>
          <p className="text-gray-500 text-sm sm:text-base">
            岡山市の高価買取に関するお客様からよく寄せられる質問をまとめました
          </p>
        </div>

        {/* Accordion */}
        <div className="flex flex-col gap-3">
          {FAQS.map((faq, i) => (
            <div
              key={i}
              className={`border rounded-xl overflow-hidden transition-all duration-700 ${
                i === 0 ? 'border-2 border-orange-400' : 'border-gray-100'
              } ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              style={{ transitionDelay: `${i * 50}ms` }}
            >
              <button
                className="w-full flex items-center gap-4 p-5 text-left bg-white hover:bg-[#B2AC88]/10 transition-colors duration-200 cursor-pointer"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <div className="w-8 h-8 flex items-center justify-center bg-[#B2AC88] rounded-full flex-shrink-0">
                  <span className="text-white text-xs font-bold">Q</span>
                </div>
                <span className="text-[#2F3630] font-medium text-sm md:text-base flex-1 text-left">{faq.q}</span>
                <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                  <i className={`ri-${openIndex === i ? 'subtract' : 'add'}-line text-[#B2AC88] text-lg transition-transform duration-200`} />
                </div>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === i ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="flex items-start gap-4 p-5 bg-[#F8F9F7]">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#B2AC88]/15 rounded-full flex-shrink-0">
                    <span className="text-[#B2AC88] text-xs font-bold">A</span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed pt-0.5">{faq.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`mt-12 text-center transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-gray-500 text-sm mb-4">上記以外のご質問もお気軽にお問い合わせください</p>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              const target = document.querySelector('#contact');
              if (target) window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - 72, behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 border border-[#D97706] text-[#D97706] hover:bg-[#D97706] hover:text-white font-medium px-8 py-3 rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-question-answer-line" />
            お問い合わせはこちら
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
